<template>
  <vx-card
    class="user-account-company-policies"
    no-shadow
  >
    <form @submit.prevent>

      <!-- Header Section - Constrained Width -->
      <div class="content-constrained">
        <h3>
          {{ $t('$UserSettings.$Account.$CompanyPolicies.TabTitle') }}
        </h3>

        <p class="text-grey-dark mt-2 mb-5">
          {{ $t('PageDescription') }}
        </p>

        <card-radio-toggle
          ref="messageTypeRadioToggle"
          v-model="model.policyType"
          :radio-name="$tc('$General.Type')"
          :radio-options="policyTypeOptions"
          :show-label="false"
          class="mt-5"
          required/>
      </div>

      <!-- Write Policy Section -->
      <div v-show="model.policyType === $enums.Tenants.POLICY_TYPE.INLINE">
        <!-- Generate Button and Disclaimer -->
        <div class="editor-header mt-base">
          <vs-button
            color="dark"
            type="border"
            size="small"
            @click="confirmGenerateTemplate">
            <feather-icon icon="FileTextIcon" svgClasses="h-4 w-4" class="mr-2" />
            {{ $t('GenerateFromTemplate') }}
          </vs-button>
          <p class="disclaimer-text mt-2 text-sm text-gray-muted leading-snug">
            {{ $t('TemplateDisclaimerText') }}
            <a
              href="#"
              class="link-contextual"
              @click.prevent="openGenerateDisclaimer"
            >
              {{ $t('GeneratedPrivacyPolicyDisclaimerLink') }}
            </a>.
          </p>
        </div>

        <!-- Alert for incomplete account details -->
        <app-alert
          v-if="!isAccountDetailsComplete"
          icon-pack="feather"
          icon="icon-alert-triangle"
          color="warning"
          class="mt-base content-constrained"
          type="default">
          <div class="pt-2 text-sm text-gray-dark leading-snug">
            <span>{{ $t('AccountDetailsIncompleteAlertMsg') }}</span>
            <a
              href="#"
              class="link-cta ml-1"
              @click.prevent="goToAccountDetails"
            >
              {{ $t('AccountDetailsIncompleteAlertLink') }}
            </a>.
          </div>
        </app-alert>

        <div class="mt-base">

          <div class="mt-base flex justify-end">
            <s-m-s-text-box-placeholders-menu
              :all-contact-attrs="[]"
              :is-write-policy="true"
              :title="$t('InsertPlaceHolders') "
              @insert="insertPlaceholder($event)"/>
          </div>

          <!-- Editor -->
          <quill-editor
            ref="policyEditor"
            v-model="model.inlinePolicy"
            :options="editorOption"
            :class="['mt-1', { 'editor-disabled': !isAccountDetailsComplete }]"
          ></quill-editor>
        </div>

        <div class="content-constrained">
          <vs-input
            v-model="model.inlinePolicy"
            type="hidden"
            class="w-full"
            :name="$t('InlinePolicyInputName')"
            v-validate="model.policyType === $enums.Tenants.POLICY_TYPE.INLINE
              ? 'required|min:500' : ''"
            data-vv-validate-on="blur|input"
            :danger="errors.has($t('InlinePolicyInputName'))"
            :danger-text="errors.first($t('InlinePolicyInputName'))"/>
        </div>
      </div>

      <!-- URL Policy Section -->
      <div class="content-constrained">
        <vs-input
          v-show="model.policyType === $enums.Tenants.POLICY_TYPE.URL"
          v-model="model.linkPolicy"
          class="w-full required mt-base"
          :name="$t('LinkPolicyInputName')"
          :label="$t('LinkPolicyInputName')"
          :placeholder="$t('LinkPolicyInputPlaceholder')"
          v-validate="model.policyType === $enums.Tenants.POLICY_TYPE.URL
          ? 'required|url' : 'url'"
          data-vv-validate-on="blur|input"
          icon="icon icon-link"
          icon-pack="feather"
          :danger="errors.has($t('LinkPolicyInputName'))"
          :danger-text="errors.first($t('LinkPolicyInputName'))"
          val-icon-danger="clear"/>
      </div>

      <vs-divider/>

      <div v-if="!asWizardStep" class="action-buttons-container">
        <div class="flex flex-col md:flex-row gap-3 w-full md:w-auto md:items-center">
          <!-- Save button - prominent when changes exist -->
          <vs-button
            class="w-full md:w-auto order-1 md:order-1"
            :color="formWasChanged ? 'primary' : 'dark'"
            :type="formWasChanged ? 'filled' : 'border'"
            @click="savePolicy">
            {{ formWasChanged ? $t('SaveChanges') : $t('$General.Save') }}
          </vs-button>

          <!-- View privacy policy - always available if policy exists -->
          <vs-button
            v-if="canViewPrivacyPolicy"
            color="primary"
            type="border"
            class="w-full md:w-auto order-2 md:order-2"
            @click="openPrivacyPolicy">
            <feather-icon icon="ExternalLinkIcon" svgClasses="h-4 w-4" class="mr-2" />
            {{ $t('ViewPrivacyPolicy') }}
          </vs-button>

          <!-- Discard changes - when there are unsaved changes -->
          <vs-button
            v-if="formWasChanged && model.policyType !== $enums.Tenants.POLICY_TYPE.URL"
            color="danger"
            type="flat"
            class="w-full md:w-auto order-3 md:order-3"
            @click="discardChanges">
            {{ $t('DiscardChanges') }}
          </vs-button>
        </div>

        <!-- Status and Auto-save indicators -->
        <div class="flex flex-col gap-2 items-center md:items-end mt-3 md:mt-0">
          <!-- Status indicator -->
          <div class="status-indicator">
            <span
              class="inline-block w-2 h-2 rounded-full"
              :class="formWasChanged ? 'bg-warning' : 'bg-success'">
            </span>
            <span :class="formWasChanged ? 'text-gray-dark' : 'text-grey-dark'">
              {{ formWasChanged ? $t('UnsavedChanges') : $t('Published') }}
            </span>
          </div>

          <!-- Auto-save indicator -->
          <div v-if="formWasChanged" class="auto-save-indicator text-xs text-grey-dark flex items-center gap-1">
            <feather-icon
              v-if="isSavingDraft"
              icon="LoaderIcon"
              svgClasses="h-3 w-3 animate-spin"
            />
            <span>{{ autoSaveStatusText }}</span>
          </div>
        </div>
      </div>
    </form>

    <vs-popup
      :title="$t('GeneratedPrivacyPolicyDisclaimerModalTitle')"
      :active="activeGeneratePrivacyPolicyDisclaimerModal"
      @close="activeGeneratePrivacyPolicyDisclaimerModal = false"
    >
      <div v-html="$t('GeneratedPrivacyPolicyDisclaimerModalMsg')"></div>
    </vs-popup>

    <vs-prompt
      color="primary"
      type="confirm"
      button-accept="accept-fill"
      button-cancel="cancel-flat"
      :title="$t('GenerateTemplateConfirmTitle')"
      :active.sync="activeGenerateTemplateConfirmPrompt"
      @accept="handleGenerateTemplateConfirmAccept"
      @cancel="handleGenerateTemplateConfirmClose"
      @close="handleGenerateTemplateConfirmClose"
    >
      <div
        class="generate-template-confirm-content text-sm text-gray-dark leading-relaxed"
        v-html="$t('GenerateTemplateConfirmMsg')"
      ></div>
    </vs-prompt>
  </vx-card>
</template>

<script>
import moment from 'moment';
import {
  mapActions,
  mapGetters,
} from 'vuex';
import { quillEditor } from 'vue-quill-editor';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.core.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.snow.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.bubble.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import Quill from 'quill';
import CardRadioToggle from '@/views/modules/_components/CardRadioToggle.vue';
import allCountries from '@/assets/utils/all-countries';
import VxTooltip from '@/layouts/components/vx-tooltip/VxTooltip.vue';

// Components
import AppAlert from '@/views/_components/AppAlert.vue';
import defaultGeneratedPolicy from './default-generated-policy';
import SMSTextBoxPlaceholdersMenu from '@/views/modules/_components/SMSTextBoxPlaceholdersMenu.vue';

// Custom Blot for placeholders that behave as atomic units
const Embed = Quill.import('blots/embed');

class PlaceholderBlot extends Embed {
  static create(value) {
    const node = super.create();
    node.setAttribute('contenteditable', false);
    node.setAttribute('data-shorthand', value.shorthand);
    node.setAttribute('data-name', value.name);
    node.setAttribute('data-attribute', value.attribute);
    node.textContent = `[${value.name}]`;
    node.classList.add('placeholder');
    node.style.cursor = 'text';
    return node;
  }

  static value(node) {
    return {
      shorthand: node.getAttribute('data-shorthand'),
      name: node.getAttribute('data-name'),
    };
  }

  length() {
    return 1;
  }
}

PlaceholderBlot.blotName = 'placeholder';
PlaceholderBlot.tagName = 'span';
PlaceholderBlot.className = 'placeholder';

Quill.register(PlaceholderBlot);

export default {
  name: 'UserAccountCompanyPolicies',
  i18n: {
    messages: {
      en: {
        PageDescription: 'Link to your privacy policy or write your own. GrapePerks will host written policies at a unique URL.',

        InsertPlaceHolders: 'Insert placeholders',

        LinkPolicyInputName: 'Policy URL',
        LinkPolicyInputPlaceholder: 'https://example.com/privacy-policy',

        InlinePolicyInputName: 'Written policy',
        InlinePolicyInputPlaceholder: 'Write your privacy policy here. You can also generate a template to get started.',

        InlinePolicyInputRadioText: 'Write policy',
        URLPolicyInputRadioText: 'Use URL',

        GenerateFromTemplate: 'Generate from template',
        TemplateDisclaimerText: 'By using the template, you acknowledge and agree to',

        AccountDetailsIncompleteAlertMsg: 'You need to complete your account details before generating a privacy policy.',
        AccountDetailsIncompleteAlertLink: 'Complete account details',

        GenerateTemplateConfirmTitle: 'Generate privacy policy from template?',
        GenerateTemplateConfirmMsg: '<p>This will override any existing content in the editor with a fresh template using your current account details. You can edit the generated template before saving.</p><p class=\"mt-5\">Note: This template is provided as a helpful tool and does not constitute legal advice.</p>',
        GenerateTemplateSuccessTitle: 'Template generated',
        GenerateTemplateSuccessMsg: 'Privacy policy template generated successfully. Please review and edit the content before saving.',

        UpdatedPrivacyPolicyTitle: 'Updated privacy policy',
        UpdatedPrivacyPolicyMsg: 'Privacy policy updated successfully.',

        GeneratedPrivacyPolicyDisclaimerLink: 'this disclaimer',
        GeneratedPrivacyPolicyDisclaimerModalTitle: 'Legal disclaimer',
        GeneratedPrivacyPolicyDisclaimerModalMsg: '<p>This template is for informational purposes only and does not constitute legal advice. By using this template, you agree to this disclaimer. You should seek independent legal advice before publishing any policies or agreements based on this template. It is your responsibility to review, modify, or remove any sections as needed to ensure compliance with applicable laws.</p><p class="mt-5">Company details (such as business name, email, and address) are automatically populated only when the template is first generated. Any subsequent updates to your company information will need to be manually updated in your privacy policy. We recommend reviewing and updating your policy whenever your company details change.</p><p class="mt-5">Use of this template does not create an attorney-client relationship between GrapePerks and any user. You should not rely on this information as a substitute for legal advice from a qualified professional in your jurisdiction. GrapePerks makes no guarantees regarding the accuracy, completeness, or legal compliance of this template and disclaims any liability for actions taken or not taken based on its contents. Additionally, GrapePerks is not responsible for any third-party content linked or referenced in relation to this template.</p>',

        ViewPrivacyPolicy: 'View privacy policy',
        SaveChanges: 'Save changes',
        DiscardChanges: 'Discard changes',
        UnsavedChanges: 'Unsaved changes',
        Published: 'Published',
        PolicyMinimumCharactersError: 'Your policy must be at least 500 characters',
      },
    },
  },
  components: {
    SMSTextBoxPlaceholdersMenu,
    CardRadioToggle,
    quillEditor,
    AppAlert,
    VxTooltip,
  },
  props: {
    asWizardStep: {
      type: Boolean,
      require: false,
      default: false,
    },
  },
  data() {
    return {
      payload: this.tenantAccountSettingsPrivacyPolicy,
      draft: this.tenantAccountSettingsPrivacyPolicyDraft,
      model: {
        policyType: this.$enums.Tenants.POLICY_TYPE.URL,
        inlinePolicy: '',
        linkPolicy: '',
      },

      activeGenerateTemplateConfirmPrompt: false,
      activeGeneratePrivacyPolicyDisclaimerModal: false,

      editorOption: {
        placeholder: this.$t('InlinePolicyInputPlaceholder'),
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            [{ align: [] }],
          ],
        },
      },

      // Track if policies have been modified
      policiesModified: false,
      isSettingPolicyModel: false,

      // Auto-save state
      draftSaveTimer: null,
      isSavingDraft: false,
      lastDraftSaveTime: null,
      autoSaveInterval: null,
      currentTime: new Date(), // Updates every second to refresh computed autoSaveStatusText

      // Cursor position tracking
      cursorPosition: 0,
    };
  },
  computed: {
    ...mapGetters({
      tenantAccountSettingsPrivacyPolicy: 'auth/tenantAccountSettingsPrivacyPolicy',
      tenantAccountSettingsPrivacyPolicyDraft: 'auth/tenantAccountSettingsPrivacyPolicyDraft',
      tenantAccountBusinessName: 'auth/tenantAccountBusinessName',
      tenantAccountBusinessEmail: 'auth/tenantAccountBusinessEmail',
      tenantAccountBusinessFullAddress: 'auth/tenantAccountBusinessFullAddress',
      tenantAccountBusinessWebsite: 'auth/tenantAccountBusinessWebsite',
      tenantAccountDetailsCountry: 'auth/tenantAccountDetailsCountry',
      getTenantFlag: 'auth/getTenantFlag',
      tenantUrlLinksSuffix: 'auth/tenantUrlLinksSuffix',
      tenantId: 'auth/tenantId',
    }),
    isAccountDetailsComplete() {
      return this.getTenantFlag('completedAccountDetails');
    },
    policyTypeOptions() {
      return [
        {
          value: this.$enums.Tenants.POLICY_TYPE.URL,
          label: this.$t('URLPolicyInputRadioText'),
        },
        {
          value: this.$enums.Tenants.POLICY_TYPE.INLINE,
          label: this.$t('InlinePolicyInputRadioText'),
        },
      ];
    },
    getCountryFullName() {
      if (!this.tenantAccountDetailsCountry) return '{{COUNTRY}}';

      const country = allCountries.find(
        (c) => c.iso2 === this.tenantAccountDetailsCountry.toUpperCase(),
      );
      return country ? country.name : this.tenantAccountDetailsCountry;
    },
    defaultGeneratedPolicyMapped() {
      let template = defaultGeneratedPolicy;

      // Handle handlebars conditionals for WEBSITE
      const hasWebsite = !!(this.tenantAccountBusinessWebsite && this.tenantAccountBusinessWebsite.trim());

      // Process {{#if WEBSITE}}...{{else}}...{{/if}} conditionals first so they
      // don't get consumed by the simpler pattern below.
      const conditionalInnerBlock = '(?:(?!\\{\\{\\/if\\}\\})[\\s\\S])*?';
      template = template.replace(new RegExp(`\\{\\{#if WEBSITE\\}\\}(${conditionalInnerBlock})\\{\\{else\\}\\}(${conditionalInnerBlock})\\{\\{\\/if\\}\\}`, 'g'), (match, ifContent, elseContent) => (hasWebsite ? ifContent : elseContent));

      // Process {{#if WEBSITE}} conditionals (using [\s\S] instead of dotAll flag)
      template = template.replace(new RegExp(`\\{\\{#if WEBSITE\\}\\}(${conditionalInnerBlock})\\{\\{\\/if\\}\\}`, 'g'), (match, content) => (hasWebsite ? content : ''));

      // Replace variables
      return template
        .replace(/\{\{DATE\}\}/g, moment().format('DD/MM/YYYY'));
    },
    formWasChanged() {
      // Explicit flag for rich-text edits (inline policies)
      if (this.policiesModified) return true;

      // Check if any non-editor fields tracked by vee-validate have changed
      const fieldsBag = this.fields || (this.$validator && this.$validator.fields);
      const fieldsChanged = fieldsBag && Object.keys(fieldsBag).some(
        (key) => fieldsBag[key] && fieldsBag[key].changed,
      );

      // For stored values that aren't controlled by Quill (policyType, URL),
      // we can safely compare against the last payload.
      const payloadChanged = !!this.payload && (
        this.payload.policyType !== this.model.policyType
        || this.payload.linkPolicy !== this.model.linkPolicy
      );

      return !!fieldsChanged || payloadChanged;
    },
    canViewPrivacyPolicy() {
      // For URL policies, only show when there is a URL to open
      if (this.model.policyType === this.$enums.Tenants.POLICY_TYPE.URL) {
        return !!this.model.linkPolicy;
      }

      // For written policies, always show.
      // The hosted page will always reflect the last saved version.
      return true;
    },
    autoSaveStatusText() {
      // Reference currentTime to make this computed property reactive to time changes
      // eslint-disable-next-line no-unused-vars
      const currentTime = this.currentTime;

      if (this.isSavingDraft) {
        return 'Saving draft...';
      }

      if (this.lastDraftSaveTime) {
        const now = new Date();
        const diffMs = now - this.lastDraftSaveTime;
        const diffSeconds = Math.floor(diffMs / 1000);
        const diffMinutes = Math.floor(diffSeconds / 60);
        const diffHours = Math.floor(diffMinutes / 60);

        if (diffSeconds < 10) {
          return 'Draft saved just now';
        } if (diffSeconds < 60) {
          return `Draft saved ${diffSeconds}s ago`;
        } if (diffMinutes < 60) {
          return `Draft saved ${diffMinutes}m ago`;
        }
        return `Draft saved ${diffHours}h ago`;
      }

      return 'Draft will be saved automatically';
    },
  },
  async created() {
    if (!this.asWizardStep) {
      // window.history.replaceState({}, null, '/account/policies');
    }

    // Custom validation messages for minimum character requirement
    this.$validator.localize('en', {
      custom: {
        [this.$t('InlinePolicyInputName')]: {
          min: this.$t('PolicyMinimumCharactersError'),
        },
      },
    });

    await this.fetchPrivacyPolicySettings();
    await this.$validator.reset();
  },
  mounted() {
    // Add beforeunload listener
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // Start interval to update current time for auto-save status text
    this.autoSaveInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 1000);

    // Set up Quill selection-change listener to track cursor position
    this.$nextTick(() => {
      const quillEditor = this.$refs.policyEditor;
      const quill = quillEditor && quillEditor.quill;
      if (quill) {
        quill.on('selection-change', (range) => {
          if (range) {
            this.cursorPosition = range.index;
          }
        });
      }
    });
  },
  async beforeDestroy() {
    // Remove beforeunload listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    // Clear any pending timers and intervals
    clearTimeout(this.draftSaveTimer);
    clearInterval(this.autoSaveInterval);

    // Final save before leaving
    if (this.draft) {
      await this.saveDraftToAPI();
    }
  },
  watch: {
    'model.policyType': function (newValue, oldValue) {
      // Reset modified flag when switching policy types
      this.policiesModified = false;
      this.saveDraft();
    },
    'model.inlinePolicy': {
      handler(newValue, oldValue) {
        if (this.isSettingPolicyModel) return;
        if (newValue === oldValue) return;
        this.policiesModified = true;
        this.saveDraft();
      },
    },
    'model.linkPolicy': {
      handler(newValue, oldValue) {
        if (this.isSettingPolicyModel) return;
        if (newValue === oldValue) return;
        this.saveDraft();
      },
    },
  },
  methods: {
    ...mapActions({
      getTenantAccountPrivacyPolicySettings: 'auth/getTenantAccountPrivacyPolicySettings',
      updateTenantAccountPrivacyPolicySettings: 'auth/updateTenantAccountPrivacyPolicySettings',
      updateTenantAccountPrivacyPolicySettingsDraft: 'auth/updateTenantAccountPrivacyPolicySettingsDraft',
      deleteTenantAccountPrivacyPolicySettingsDraft: 'auth/deleteTenantAccountPrivacyPolicySettingsDraft',
    }),
    async fetchPrivacyPolicySettings() {
      this.$vs.loading({ type: 'radius' });
      const data = await this.getTenantAccountPrivacyPolicySettings();
      this.$vs.loading.close();
      this.payload = data.privacyPolicy;
      this.draft = data.privacyPolicyDraft;

      // If no draft in DB, check localStorage as backup
      if (!this.draft) {
        try {
          const localStorageKey = this.getLocalStorageKey();
          const localDraft = localStorage.getItem(localStorageKey);
          if (localDraft) {
            this.draft = JSON.parse(localDraft);
          }
        } catch (error) {
          console.error('Error reading from localStorage:', error);
        }
      }

      this.setModel();
    },
    setModel(params) {
      const { preservePolicyType, ignoreDraft = false } = params || {};
      this.isSettingPolicyModel = true;

      const useDraft = !ignoreDraft && !!this.draft;
      const source = useDraft ? this.draft : this.payload;

      // Policy type
      this.model.policyType = source
        ? source.policyType || this.model.policyType
        : this.model.policyType;

      // Inline policy: always reset to what is stored (or empty string)
      // Note: This now handles both written policies and generated templates
      if (source) {
        this.model.inlinePolicy = source.inlinePolicy || '';
      }

      // URL policy: reset to stored value (or empty string)
      if (source) {
        this.model.linkPolicy = source.linkPolicy || '';
      }

      // Allow Quill to normalize content without marking the form as changed
      this.$nextTick(() => {
        if (typeof preservePolicyType !== 'undefined') {
          this.model.policyType = preservePolicyType;
        }

        this.isSettingPolicyModel = false;
        // If we loaded from a draft, mark as modified so "Unsaved changes" appears
        this.policiesModified = useDraft;
      });
    },
    saveDraft() {
      if (this.isSettingPolicyModel) return;

      this.draft = {
        policyType: this.model.policyType,
        inlinePolicy: this.model.inlinePolicy,
        linkPolicy: this.model.linkPolicy,
      };

      // Save to localStorage immediately (backup)
      try {
        const localStorageKey = this.getLocalStorageKey();
        localStorage.setItem(localStorageKey, JSON.stringify(this.draft));
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }

      // Debounced save to API (wait 2 seconds after last change)
      clearTimeout(this.draftSaveTimer);
      this.draftSaveTimer = setTimeout(async () => {
        await this.saveDraftToAPI();
      }, 2000);
    },
    confirmGenerateTemplate() {
      // Check if account details are complete
      if (!this.isAccountDetailsComplete) {
        this.$vs.dialog({
          type: 'alert',
          color: 'warning',
          title: this.$t('AccountDetailsIncompleteAlertMsg'),
          text: this.$t('AccountDetailsIncompleteAlertMsg'),
          acceptText: this.$t('AccountDetailsIncompleteAlertLink'),
          accept: () => {
            this.goToAccountDetails();
          },
        });
        return;
      }

      this.activeGenerateTemplateConfirmPrompt = true;
    },
    handleGenerateTemplateConfirmAccept() {
      this.generateTemplate();
      this.activeGenerateTemplateConfirmPrompt = false;
    },
    handleGenerateTemplateConfirmClose() {
      this.activeGenerateTemplateConfirmPrompt = false;
    },
    getPlaceholderMetadata(label) {
      // Check Business Placeholders
      const businessEntry = Object.entries(this.$enums.Campaign.BusinessPlaceholders)
        .find(([, value]) => value === label);

      if (businessEntry) {
        const [key] = businessEntry;
        return {
          name: label,
          shorthand: `[${label}]`,
          attribute: this.$enums.Campaign.BusinessPlaceholdersAttributeName[key],
        };
      }

      // Check Special Placeholders
      if (label === this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK) {
        return {
          name: label,
          shorthand: `[${label}]`,
          attribute: this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK,
        };
      }

      return null;
    },
    setEditorContentWithPlaceholders(htmlContent) {
      const quillEditor = this.$refs.policyEditor;
      const quill = quillEditor && quillEditor.quill;
      if (!quill) return;

      const delta = quill.clipboard.convert(htmlContent);
      const Delta = Quill.import('delta');
      const newDelta = new Delta();

      delta.ops.forEach((op) => {
        if (typeof op.insert === 'string') {
          let text = op.insert;
          const regex = /\[(.*?)\]/g;
          let match;
          let lastIndex = 0;

          // Process matches
          while ((match = regex.exec(text)) !== null) {
            // Text before match
            if (match.index > lastIndex) {
              newDelta.insert(text.substring(lastIndex, match.index), op.attributes);
            }

            const label = match[1];
            const meta = this.getPlaceholderMetadata(label);

            if (meta) {
              // Known placeholder -> Insert Embed
              newDelta.insert({
                placeholder: {
                  shorthand: meta.shorthand,
                  name: meta.name,
                  attribute: meta.attribute,
                },
              }); // Embeds usually don't take text attributes like bold, but structure is preserved
            } else {
              // Unknown placeholder -> Keep as text
              newDelta.insert(match[0], op.attributes);
            }

            lastIndex = regex.lastIndex;
          }

          // Remaining text
          if (lastIndex < text.length) {
            newDelta.insert(text.substring(lastIndex), op.attributes);
          }
        } else {
          // Pass through other ops (images, etc, or other embeds)
          newDelta.push(op);
        }
      });

      quill.setContents(newDelta);
    },
    generateTemplate() {
      // Generate the template with current company details
      // using setContents triggers watcher implicitly which handles draft saving
      this.isSettingPolicyModel = true;
      this.setEditorContentWithPlaceholders(this.defaultGeneratedPolicyMapped);
      this.isSettingPolicyModel = false;

      this.$nextTick(() => {
        // Ensure modified flag is set even if watcher didn't trigger for some reason (e.g. content same)
        // although generating template usually changes content.
        this.policiesModified = true;
        this.saveDraft();
      });

      this.$showSuccessActionNotification({
        title: this.$t('GenerateTemplateSuccessTitle'),
        text: this.$t('GenerateTemplateSuccessMsg'),
      });
    },
    async savePolicy() {
      const formIsValid = await this.$validator.validateAll();

      if (this.model.policyType === this.$enums.Tenants.POLICY_TYPE.URL) {
        this.model.inlinePolicy = '';
      } else {
        this.model.linkPolicy = '';
      }

      if (formIsValid) {
        this.$vs.loading({ type: 'radius' });
        const resp = await this.updateTenantAccountPrivacyPolicySettings(this.model);
        this.$vs.loading.close();

        // Update payload but DON'T call setModel() to avoid overriding user's changes
        // Ensure payload reflects the saved model in case response is partial
        this.payload = {
          ...resp.data,
          policyType: this.model.policyType,
          inlinePolicy: this.model.inlinePolicy,
          linkPolicy: this.model.linkPolicy,
        };

        // Clear draft from both DB and localStorage
        await this.deleteTenantAccountPrivacyPolicySettingsDraft();
        this.draft = null;
        this.clearLocalStorageDraft();

        // Reset auto-save state
        this.lastDraftSaveTime = null;
        this.isSavingDraft = false;

        // Reset the modified flag since we just saved
        this.policiesModified = false;

        this.$showSuccessActionNotification({
          title: this.$t('UpdatedPrivacyPolicyTitle'),
          text: this.$t('UpdatedPrivacyPolicyMsg'),
        });
      }

      return formIsValid;
    },
    goToAccountDetails() {
      this.$root.$emit('switch-to-account-tab', 'details');
    },
    openPrivacyPolicy() {
      if (this.model.policyType === this.$enums.Tenants.POLICY_TYPE.URL) {
        // Open external URL
        if (this.model.linkPolicy) {
          window.open(this.model.linkPolicy, '_blank');
        }
      } else {
        // Open hosted privacy policy
        const url = `${window.location.origin}/privacy-policy/${this.tenantUrlLinksSuffix}`;
        window.open(url, '_blank');
      }
    },
    discardChanges() {
      // Reset to the last saved state
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Discard changes?',
        text: 'Are you sure you want to discard all unsaved changes?',
        accept: () => {
          const currentPolicyType = this.model.policyType;
          this.deleteTenantAccountPrivacyPolicySettingsDraft();
          this.draft = null;
          this.clearLocalStorageDraft();

          // Reset auto-save state
          this.lastDraftSaveTime = null;
          this.isSavingDraft = false;

          this.setModel({ preservePolicyType: currentPolicyType, ignoreDraft: true });
          this.policiesModified = false;
          this.$validator.reset();
        },
      });
    },
    openGenerateDisclaimer() {
      this.activeGeneratePrivacyPolicyDisclaimerModal = true;
    },
    // Helper methods for auto-save
    async saveDraftToAPI() {
      if (!this.draft || this.isSavingDraft) return;

      try {
        this.isSavingDraft = true;
        await this.updateTenantAccountPrivacyPolicySettingsDraft(this.draft);
        this.lastDraftSaveTime = new Date();
      } catch (error) {
        console.error('Error saving draft to API:', error);
      } finally {
        this.isSavingDraft = false;
      }
    },
    handleBeforeUnload(event) {
      if (this.formWasChanged && this.draft) {
        // Save to localStorage as backup
        try {
          const localStorageKey = this.getLocalStorageKey();
          localStorage.setItem(localStorageKey, JSON.stringify(this.draft));
        } catch (error) {
          console.error('Error saving to localStorage on beforeunload:', error);
        }
      }
    },
    getLocalStorageKey() {
      return `privacyPolicyDraft_${this.tenantId}`;
    },
    clearLocalStorageDraft() {
      try {
        const localStorageKey = this.getLocalStorageKey();
        localStorage.removeItem(localStorageKey);
      } catch (error) {
        console.error('Error clearing localStorage draft:', error);
      }
    },
    insertPlaceholder(placeholder) {
      const quillEditor = this.$refs.policyEditor;
      const quill = quillEditor && quillEditor.quill;
      if (!quill) return;

      // Try to get current cursor position, otherwise use saved position
      const selection = quill.getSelection();
      const cursorIndex = selection ? selection.index : this.cursorPosition;

      // Insert the placeholder embed at cursor position
      quill.insertEmbed(cursorIndex, 'placeholder', {
        shorthand: placeholder.shorthand,
        name: placeholder.name,
        attribute: placeholder.attribute,
      });

      // Insert a space after placeholder for convenience
      quill.insertText(cursorIndex + 1, ' ');

      // Move cursor after the placeholder and space, and scroll into view
      this.$nextTick(() => {
        const newCursorIndex = cursorIndex + 2;
        quill.setSelection(newCursorIndex, 0);
        quill.focus();

        // Scroll cursor into view
        const editorElement = quill.root;
        const newSelection = quill.getSelection();
        if (newSelection) {
          const bounds = quill.getBounds(newSelection.index);
          if (bounds) {
            // Scroll to ensure cursor is visible
            const scrollTop = bounds.top - editorElement.offsetHeight / 2;
            editorElement.scrollTop = Math.max(0, scrollTop);
          }
        }

        // Update saved cursor position
        this.cursorPosition = newCursorIndex;
      });
    },
  },
};
</script>

<style lang="scss">
.user-account-company-policies {
  // Max width for the whole container on large screens to avoid looking odd
  max-width: 1200px;
  margin: 0 auto;

  // Constrain specific sections for readability
  .content-constrained {
    max-width: 800px;
  }

  // Action buttons container responsiveness
  .action-buttons-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;

    @media (max-width: 767px) {
      flex-direction: column;
      align-items: stretch;

      .status-indicator {
        order: -1;
        justify-content: center;
        margin-bottom: 0.5rem;
        width: 100%;
      }
    }
  }

  .status-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .generate-template-confirm-content p {
    margin-bottom: 0.75rem;
  }

  .generate-template-confirm-content p:last-child {
    margin-bottom: 0;
  }

  // Editor header section
  .editor-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 1rem;
  }

  .quill-editor {
    min-height: 250px;
    border: none; // remove wrapper border to avoid double borders
  }
  .ql-container {
    min-height: 200px;
    max-height: 400px;
    overflow-y: auto;
  }

  // Disabled editor styling
  .editor-disabled {
    opacity: 0.6;
    pointer-events: none;

    .ql-toolbar {
      opacity: 0.5;
      pointer-events: none;
    }

    .ql-container {
      background-color: #f8f9fa;
      color: #6c757d;
    }

    .ql-editor {
      cursor: not-allowed;
      background-color: #f8f9fa !important;
    }
  }

  // Placeholder styling
  .ql-editor {
    .placeholder {
      font-weight: 600;
      color: rgba(var(--vs-primary), 1);
      cursor: text !important;
      user-select: all;
      white-space: nowrap;
      display: inline-block;

      &::selection {
        background-color: rgba(var(--vs-primary), 0.2);
      }
    }
  }

}
</style>
